const BOSSDB_PAPER_DOI = "10.3389/fninf.2022.828787";
const BOSSDB_PAPER_URL = `https://doi.org/${BOSSDB_PAPER_DOI}`;
const BOSSDB_RRID = "RRID:SCR_017273";

const BOSSDB_PAPER_APA = `Hider Jr., R., Kleissas, D., Gion, T., Xenes, D., Matelsky, J., Pryor, D., Rodriguez, L., Johnson, E. C., Gray-Roncal, W., & Wester, B. (2022). The Brain Observatory Storage Service and Database (BossDB): A cloud-native approach for petascale neuroscience discovery. Frontiers in Neuroinformatics, 16, 828787. ${BOSSDB_PAPER_URL}`;

const BOSSDB_PAPER_MLA = `Hider Jr., Robert, et al. "The Brain Observatory Storage Service and Database (BossDB): A Cloud-Native Approach for Petascale Neuroscience Discovery." Frontiers in Neuroinformatics, vol. 16, 2022, article 828787. ${BOSSDB_PAPER_URL}.`;

const BOSSDB_PAPER_BIBTEX = `@article{Hider2022BossDB,
  author = {Hider Jr., Robert and Kleissas, Dean and Gion, Timothy and Xenes, Daniel and Matelsky, Jordan and Pryor, Derek and Rodriguez, Luis and Johnson, Erik C. and Gray-Roncal, William and Wester, Brock},
  title = {{The Brain Observatory Storage Service and Database (BossDB): A Cloud-Native Approach for Petascale Neuroscience Discovery}},
  journal = {Frontiers in Neuroinformatics},
  volume = {16},
  pages = {828787},
  year = {2022},
  doi = {${BOSSDB_PAPER_DOI}},
  url = {${BOSSDB_PAPER_URL}}
}`;

const normalizeDoi = (doi) => {
    return (doi || "")
        .toString()
        .trim()
        .replace(/^doi:\s*/i, "")
        .replace(/^https?:\/\/(dx\.)?doi\.org\//i, "")
        .replace(/^doi\.org\//i, "");
};

const doiToUrl = (doi) => {
    const normalizedDoi = normalizeDoi(doi);
    return normalizedDoi ? `https://doi.org/${normalizedDoi}` : "";
};

const toValidDate = (value) => {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "string") {
        const dateParts = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateParts) {
            const [, year, month, day] = dateParts;
            return new Date(
                Number(year),
                Number(month) - 1,
                Number(day)
            );
        }
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatIsoDate = (value) => {
    const date = toValidDate(value);
    if (!date) {
        return "";
    }

    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
};

const formatApaDate = (value) => {
    const date = toValidDate(value);
    return date
        ? date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : "";
};

const formatMlaDate = (value) => {
    const date = toValidDate(value);
    if (!date) {
        return "";
    }

    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${date.getDate()} ${month} ${date.getFullYear()}`;
};

const projectCitationKey = (project) => {
    const rawValue = project?.id || project?.name || "bossdb_dataset";
    return rawValue
        .toString()
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "bossdb_dataset";
};

const projectIdentifierUrl = (project) => {
    return doiToUrl(project?.doi) || `https://bossdb.org/project/${project?.id}`;
};

const projectCitationTitle = (project) => {
    return project?.name || project?.citationName || "Untitled";
};

const projectPublicationYear = (project) => {
    const projectDate = toValidDate(project?.date);
    if (projectDate) {
        return `${projectDate.getFullYear()}`;
    }

    const citationYear = project?.citationName
        ?.toString()
        .match(/\b(19|20)\d{2}\b/);

    return citationYear ? citationYear[0] : "";
};

const buildProjectBibtex = (project) => {
    const title = projectCitationTitle(project);
    const doi = normalizeDoi(project?.doi);
    const url = projectIdentifierUrl(project);
    const year = projectPublicationYear(project);
    const createdDate = formatIsoDate(project?.date);
    const accessedDate = formatIsoDate(new Date());
    const lines = [
        `@misc{${projectCitationKey(project)},`,
        `  title = {${title}},`,
        year ? `  year = {${year}},` : null,
        createdDate ? `  date = {${createdDate}},` : null,
        accessedDate ? `  urldate = {${accessedDate}},` : null,
        `  publisher = {BossDB.org},`,
        doi ? `  doi = {${doi}},` : null,
        `  url = {${url}},`,
        `  note = {Dataset; ${BOSSDB_RRID}}`,
        `}`
    ];

    return lines.filter(Boolean).join("\n");
};

const buildProjectApa = (project) => {
    const createdDate = formatApaDate(project?.date);
    const accessedDate = formatApaDate(new Date());
    const prefix = `${projectCitationTitle(project)} [Dataset].`;
    const createdSegment = createdDate ? ` (${createdDate}).` : "";
    const accessedSegment = accessedDate ? ` Accessed ${accessedDate}.` : "";
    return `${prefix}${createdSegment} BossDB.org. ${projectIdentifierUrl(project)}.${accessedSegment} ${BOSSDB_RRID}.`;
};

const buildProjectMla = (project) => {
    const createdDate = formatMlaDate(project?.date);
    const accessedDate = formatMlaDate(new Date());
    const createdSegment = createdDate ? `, ${createdDate},` : ",";
    const accessedSegment = accessedDate ? ` Accessed ${accessedDate}.` : "";
    return `${projectCitationTitle(project)}. BossDB.org${createdSegment} ${projectIdentifierUrl(project)}.${accessedSegment} ${BOSSDB_RRID}.`;
};

const buildProjectCitation = (project, format) => {
    switch (format) {
        case "bibtex":
            return buildProjectBibtex(project);
        case "mla":
            return buildProjectMla(project);
        case "apa":
        default:
            return buildProjectApa(project);
    }
};

const buildCombinedCitation = (project, format) => {
    const projectCitation = buildProjectCitation(project, format);

    switch (format) {
        case "bibtex":
            return `${projectCitation}\n\n${BOSSDB_PAPER_BIBTEX}`;
        case "mla":
            return `${projectCitation}\n\n${BOSSDB_PAPER_MLA}`;
        case "apa":
        default:
            return `${projectCitation}\n\n${BOSSDB_PAPER_APA}`;
    }
};

export {
    BOSSDB_PAPER_APA,
    BOSSDB_PAPER_BIBTEX,
    BOSSDB_PAPER_DOI,
    BOSSDB_PAPER_MLA,
    BOSSDB_PAPER_URL,
    buildCombinedCitation,
    buildProjectCitation,
    buildProjectApa,
    buildProjectBibtex,
    buildProjectMla,
    doiToUrl,
};
